import type {
    RoomActivePoll,
    RoomDoorAnswer,
    RoomJoin,
    RoomMessage,
    RoomModeratorState,
    WebinarRoomConnectionDto,
    WebinarRoomHeartbeatMode,
    WebinarRoomModerateRequestDto,
    WebinarRoomPlayerState,
    WebinarRoomUrlDto,
} from '../types'

/** What a viewer's page reports every heartbeat, beside the token and its own device and tab. */
export interface RoomHeartbeat {
    Mode: WebinarRoomHeartbeatMode
    PlayerState: WebinarRoomPlayerState
    Visible: boolean
    PositionSeconds: number | null
}

/** The door's two fields; the room's ids come from the page itself. */
export interface RoomDoorEntry {
    Name: string
    Email: string
}

/** A moderation request without the token, which the transport adds when it has one. */
export type RoomModeration = Omit<WebinarRoomModerateRequestDto, 'Token'>

/** What only the web itself knows about the session's set-up, for the readiness list before it goes live. */
export interface RoomReadiness {
    /** The sign-off gate's own words for what stands in the recording's way (`OnlineEvent.Recording`), or null when nothing does. */
    recordingBlocker: string | null
    /** Every speaker on the web has had their host link emailed. */
    hostLinksAllEmailed: boolean
}

/**
 * One way into one room. The viewer bundle reaches the room through the public edge with the token from its link;
 * the dashboard reaches it through the core API as the signed-in moderator. Everything shared talks to this, never
 * to fetch or apiClient, so the same screens run on both.
 *
 * Optional members exist on one side only: the door, heartbeats and poll answers are a link's (a moderator neither
 * registers nor counts); the preview link, the transcript and the web's readiness are the dashboard's.
 */
export interface RoomTransport {
    join(): Promise<RoomJoin>
    negotiate(): Promise<WebinarRoomConnectionDto>
    history(): Promise<RoomMessage[]>
    /** The moderator's view of the room: people, presenters, poll runs. Moderators and presenters only. */
    moderatorState(): Promise<RoomModeratorState>
    chat(text: string, asQuestion: boolean): Promise<RoomMessage>
    /**
     * A message in one registrant's private conversation. Hosts name the registrant; a viewer's reply always goes in
     * their own conversation, which only a host can start, so a viewer passes none.
     */
    privateMessage(text: string, registrationId?: string): Promise<RoomMessage>
    moderate(request: RoomModeration): Promise<void>
    door?(entry: RoomDoorEntry): Promise<RoomDoorAnswer>
    /** The join again, after the person chose "Continue as …" on an emailed door link: it moves the seat to this browser. */
    takeSeat?(): Promise<RoomJoin>
    heartbeat?(beat: RoomHeartbeat): Promise<{ RequiresDoor: boolean }>
    /** The last heartbeat as the page goes away: fire and forget, and it outlives the page. */
    finalHeartbeat?(beat: RoomHeartbeat): void
    pollAnswer?(pollId: string, optionIndex: number): Promise<RoomActivePoll>
    preview?(): Promise<WebinarRoomUrlDto>
    transcript?(): Promise<{ blob: Blob; filename?: string }>
    readiness?(): Promise<RoomReadiness>
}

export type RoomRequestFailure =
    /** A bad or revoked token, or a room that is not there. The routes answer a bare 404 on purpose. */
    | 'not-found'
    /** A 422: the API said why, in words a person can act on. */
    | 'refused'
    | 'rate-limited'
    /** Never answered: offline, a dropped connection, or the API down. */
    | 'unreachable'
    | 'failed'

/** Every failed room call, from either transport, so the screens handle one shape. */
export class RoomRequestError extends Error {
    readonly failure: RoomRequestFailure
    readonly status: number
    /** The 422's messages, in order. Empty for every other failure. */
    readonly messages: string[]
    readonly retryAfterSeconds: number | null

    constructor(failure: RoomRequestFailure, status: number, messages: string[] = [], retryAfterSeconds: number | null = null) {
        super(messages[0] ?? `The room request failed (${failure}, ${status}).`)
        this.name = 'RoomRequestError'
        this.failure = failure
        this.status = status
        this.messages = messages
        this.retryAfterSeconds = retryAfterSeconds
    }
}

/** The failure for a status the API answered with. */
export function failureForStatus(status: number): RoomRequestFailure {
    if (status === 404) return 'not-found'
    if (status === 422) return 'refused'
    if (status === 429) return 'rate-limited'
    if (status === 0) return 'unreachable'
    return 'failed'
}

export function isRoomRequestError(error: unknown, failure?: RoomRequestFailure): error is RoomRequestError {
    return error instanceof RoomRequestError && (failure === undefined || error.failure === failure)
}
